import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { ChannelIcon } from '../../components/ui/ChannelIcon';
import type { PostSummary } from '../../types/publishing';
import { expandPostToRows, filterPosts, formatRowTime } from '../../lib/publishing/calendarBuckets';
import {
  loadGroupMode,
  loadSelectedAccountIds,
  saveGroupMode,
  saveSelectedAccountIds,
  selectedAccountForCreate,
  type MenuGroupMode,
} from '../../lib/publishing/calendarSelection';
import { deletePost, listPostsForAccounts, listPublishingAccounts } from './api';
import { ConfirmDeletePostModal } from './ConfirmDeletePostModal';
import { PublishingAccountRail } from './PublishingAccountRail';

// Publishing — the standalone Drafts view (2026-07-19, sidebar sub-nav §B2).
// Promotes what was only the calendar's unscheduled tray into its own page.
//
// SELECTION-SCOPED (2026-07-29): like the calendar, it shows drafts only for the
// selected accounts, sharing the SAME session-persisted selection + Group-by menu
// (so switching between the two keeps the view). It reuses the scoped query +
// pure filter layer (listPostsForAccounts + filterPosts). One deliberate
// difference from the calendar tray: the tray shows only DATELESS drafts, whereas
// this page shows EVERY draft — a draft parked on a date is still a draft.

export function PublishingDraftsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const accounts = useQuery({ queryKey: ['publishing-accounts'], queryFn: listPublishingAccounts });
  const [selectedIds, setSelectedIdsState] = useState<Set<string>>(
    () => new Set(loadSelectedAccountIds()),
  );
  const [groupMode, setGroupModeState] = useState<MenuGroupMode>(() => loadGroupMode());
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<PostSummary | null>(null);

  const setSelectedIds = (next: Set<string>) => {
    setSelectedIdsState(next);
    saveSelectedAccountIds(next);
  };
  const setGroupMode = (mode: MenuGroupMode) => {
    setGroupModeState(mode);
    saveGroupMode(mode);
  };

  const selectedIdList = useMemo(() => [...selectedIds], [selectedIds]);
  const hasSelection = selectedIdList.length > 0;

  const posts = useQuery({
    queryKey: ['publishing-posts', 'scoped', [...selectedIdList].sort()],
    queryFn: () => listPostsForAccounts(selectedIdList),
    enabled: hasSelection,
  });

  const remove = useMutation({
    mutationFn: (draftId: string) => deletePost(draftId),
    onSuccess: () => {
      setDeleteTarget(null);
      toast.success('Draft deleted');
      qc.invalidateQueries({ queryKey: ['publishing-posts'] });
    },
  });

  const accountName = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accounts.data ?? []) m.set(a.id, a.name);
    return m;
  }, [accounts.data]);

  const drafts = useMemo(
    () =>
      filterPosts(posts.data ?? [], { statuses: ['draft'], search })
        // Most recently touched first — the tray's ordering.
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [posts.data, search],
  );

  // Pre-select the composer's account only when exactly one is selected.
  const createAccountId = selectedAccountForCreate(selectedIdList);

  return (
    <div>
      <PageHeader
        title="Drafts"
        description="Every unpublished draft — scheduled or not. Open one to finish and publish it."
        actions={
          <Button asChild>
            <Link to={createAccountId ? `/publishing/new?account=${createAccountId}` : '/publishing/new'}>
              New post
            </Link>
          </Button>
        }
      />

      <div className="flex gap-6">
        <div className="w-64 shrink-0">
          <PublishingAccountRail
            accounts={accounts.data ?? []}
            selectedIds={selectedIds}
            onChange={setSelectedIds}
            groupMode={groupMode}
            onGroupModeChange={setGroupMode}
            loading={accounts.isLoading}
          />
        </div>

        <div className="min-w-0 flex-1">
          <div className="mb-4 flex items-center gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search captions…"
              aria-label="Search draft captions"
              className="h-9 w-64"
              disabled={!hasSelection}
            />
            {hasSelection && (
              <span className="text-sm text-slate-500">
                {drafts.length} draft{drafts.length === 1 ? '' : 's'}
              </span>
            )}
          </div>

          {!hasSelection ? (
            <p className="rounded-lg border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-400">
              Select one or more locations to view their drafts.
            </p>
          ) : posts.isLoading ? (
            <CenteredSpinner />
          ) : drafts.length === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-200 px-4 py-10 text-center text-sm text-slate-400">
              {search ? 'No drafts match that search.' : 'No drafts for the selected locations.'}
            </p>
          ) : (
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {drafts.map((draft) => (
                <DraftRow
                  key={draft.id}
                  draft={draft}
                  accountName={accountName.get(draft.kyckAccountId) ?? ''}
                  onOpen={() => navigate(`/publishing/${draft.id}/edit`)}
                  onDelete={() => setDeleteTarget(draft)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {deleteTarget && (
        <ConfirmDeletePostModal
          scheduled={deleteTarget.zernioPostId !== null}
          busy={remove.isPending}
          errorText={remove.error instanceof Error ? remove.error.message : null}
          onConfirm={() => remove.mutate(deleteTarget.id)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

function DraftRow({
  draft,
  accountName,
  onOpen,
  onDelete,
}: {
  draft: PostSummary;
  accountName: string;
  onOpen: () => void;
  onDelete: () => void;
}) {
  // Reuse the calendar's fan-out for the destination glyphs, so a draft shows
  // exactly the destinations its calendar lines would.
  const rows = expandPostToRows(draft);
  const scheduled = draft.scheduledFor
    ? `${new Date(draft.scheduledFor).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
      })} · ${formatRowTime(draft.scheduledFor)}`
    : 'No date';

  // A container (not a <button>) so the trailing Delete can be its own button —
  // nesting a button inside a button is invalid HTML.
  return (
    <div className="group flex w-full items-center gap-3 pr-2 transition-colors hover:bg-slate-50">
      <button
        type="button"
        onClick={onOpen}
        className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3 text-left"
      >
        <span className="flex w-16 shrink-0 items-center gap-0.5">
          {rows.map((row) =>
            row.platform ? (
              <ChannelIcon key={row.rowKey} channel={row.platform} className="h-4 w-4" />
            ) : null,
          )}
        </span>
        <span className="min-w-0 flex-1">
          {accountName && (
            <span className="block text-xs font-medium text-slate-500">{accountName}</span>
          )}
          <span className="block truncate text-sm text-slate-800">
            {draft.content || '(no caption)'}
          </span>
        </span>
        <span className="shrink-0 text-xs text-slate-400">{scheduled}</span>
      </button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Delete draft"
        title="Delete draft"
        className="shrink-0 text-slate-400 hover:text-red-600"
        onClick={onDelete}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
